/* ═══════════════════════════════════════════════════════════════
   AddMemberPanel — the Directory tab's right-side panel for adding a
   new team member. Replaces AddMemberForm.tsx's inline expanding top
   bar per the Team Management UX Overhaul (2026-08-18): side panels
   are THE pattern for People. Every field and both actions from the
   old top-bar form are preserved verbatim: Email*, Name, Role,
   Title, "Add Member" (direct create) and "Add & Send Invite".

   Name field split into First / Middle (optional) / Last (No Full-Name
   Input/Write Law, 2026-08-19) — was a single "Full name" text input
   that got guess-split on submit; now the parts are typed directly and
   land in member_profiles the same way PersonPanel's fields do. Same
   flex-2/1.5/2 row layout as PersonPanel's split so the two forms read
   as one system.
   ═══════════════════════════════════════════════════════════════ */

import { X, UserPlus } from 'lucide-react';
import { SearchableSelect } from '../../components/SearchableSelect';
import { Button } from '../../components/ui/button';
import { Icon } from '../../components/ui/Icon';
import { rem } from '../../lib/rem';
import type { Role, BillingRole } from '../../../shared/types';
import { sectionHeading, fieldBlock, fieldLabel, inputStyle } from './styles';

export interface AddMemberPanelProps {
  roles: Role[];
  billingRoles: BillingRole[];
  inviteEmail: string;
  inviteFirstName: string;
  inviteMiddleName: string;
  inviteLastName: string;
  inviteRoleId: string;
  inviteBillingRoleId: string;
  /** team_members.manage_roles — required by the server to set a role on
   *  member CREATE, same as edit. Governs whether "Add Member" (direct
   *  create) may carry the selected role. */
  canManageRoles: boolean;
  setInviteEmail: (v: string) => void;
  setInviteFirstName: (v: string) => void;
  setInviteMiddleName: (v: string) => void;
  setInviteLastName: (v: string) => void;
  setInviteRoleId: (v: string) => void;
  setInviteBillingRoleId: (v: string) => void;
  onClose: () => void;
  addMemberDirect: () => void;
  inviteMember: () => void;
}

export function AddMemberPanel({
  roles,
  billingRoles,
  inviteEmail,
  inviteFirstName,
  inviteMiddleName,
  inviteLastName,
  inviteRoleId,
  inviteBillingRoleId,
  canManageRoles,
  setInviteEmail,
  setInviteFirstName,
  setInviteMiddleName,
  setInviteLastName,
  setInviteRoleId,
  setInviteBillingRoleId,
  onClose,
  addMemberDirect,
  inviteMember,
}: AddMemberPanelProps) {
  return (
    <div
      style={{
        width: rem(440),
        flexShrink: 0,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderLeft: '1px solid var(--bd)',
        background: 'var(--bg)',
      }}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 'var(--space-2)',
          padding: 'var(--space-4) var(--space-4) var(--space-3)',
          borderBottom: '1px solid var(--bd)',
          flexShrink: 0,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
          <Icon icon={UserPlus} em={1.2308} aria-hidden="true" style={{ color: 'var(--ac)' }} />
          <div style={{ fontSize: 'var(--font-size-lg)', fontWeight: 700 }}>
            Add a New Team Member
          </div>
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 shrink-0"
          onClick={onClose}
          title="Close"
          aria-label="Close add member panel"
        >
          <Icon icon={X} em={1.2308} />
        </Button>
      </div>

      {/* Scrollable body */}
      <div style={{ flex: 1, overflow: 'auto', padding: 'var(--space-4)' }}>
        <div style={sectionHeading}>Details</div>

        <label style={fieldBlock}>
          <span style={fieldLabel}>Email *</span>
          <input
            value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)}
            placeholder="name@company.com"
            autoFocus
            style={inputStyle}
          />
        </label>

        <div style={{ display: 'flex', gap: 'var(--space-2)', marginBottom: 'var(--space-3)' }}>
          <label style={{ flex: 2 }}>
            <span style={fieldLabel}>First Name</span>
            <input
              value={inviteFirstName}
              onChange={(e) => setInviteFirstName(e.target.value)}
              placeholder="e.g. Jane (optional)"
              style={inputStyle}
            />
          </label>
          <label style={{ flex: 1.5 }}>
            <span style={fieldLabel}>Middle</span>
            <input
              value={inviteMiddleName}
              onChange={(e) => setInviteMiddleName(e.target.value)}
              placeholder="optional"
              style={inputStyle}
            />
          </label>
          <label style={{ flex: 2 }}>
            <span style={fieldLabel}>Last Name</span>
            <input
              value={inviteLastName}
              onChange={(e) => setInviteLastName(e.target.value)}
              placeholder="e.g. Smith (optional)"
              style={inputStyle}
            />
          </label>
        </div>

        <label style={fieldBlock}>
          <span style={fieldLabel}>Role</span>
          <SearchableSelect
            value={inviteRoleId}
            options={roles.map((r) => ({ value: r.id, label: r.name }))}
            onChange={(v) => setInviteRoleId(v)}
            placeholder="-- Select role --"
            searchPlaceholder="Search roles…"
            clearable={false}
          />
        </label>

        <label style={{ ...fieldBlock, marginBottom: rem(20) }}>
          <span style={fieldLabel}>Title</span>
          <SearchableSelect
            value={inviteBillingRoleId}
            options={billingRoles.map((br) => ({ value: br.id, label: br.name }))}
            onChange={(v) => setInviteBillingRoleId(v)}
            placeholder="-- None --"
            searchPlaceholder="Search titles…"
          />
        </label>

        <div
          style={{
            display: 'flex',
            gap: 'var(--space-2)',
            flexWrap: 'wrap',
            marginBottom: 'var(--space-4)',
          }}
        >
          <Button onClick={addMemberDirect}>Add Member</Button>
          <Button variant="secondary" onClick={inviteMember}>
            Add &amp; Send Invite
          </Button>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
        </div>

        <p
          style={{
            fontSize: 'var(--font-size-sm)',
            color: 'var(--tm)',
            margin: 0,
            lineHeight: 1.5,
          }}
        >
          <strong>Add Member</strong> creates the person immediately — no email sent; they can sign
          up later with your firm code and their account links by email.
          <strong> Add &amp; Send Invite</strong> also emails them a signup invitation
          pre-configured with the role and title you set here.
          {!canManageRoles && inviteRoleId && (
            <>
              {' '}
              Role only requires role-management permission to apply — using{' '}
              <strong>Add Member</strong> right now, an admin will need to set their role afterward.
            </>
          )}
        </p>
      </div>
    </div>
  );
}
